import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useTaskStore } from './taskStore';
import { useEventStore } from './eventStore';
import type { Task } from '../types';
import type { InboxItem, InboxKind, InboxDestination, InboxOutcome, InboxStage, InboxTarget } from '../types/inbox';
import { routeInboxAction, type RoutableType } from '../utils/inboxRouting';
import { attachInboxItem } from '../utils/inboxAttach';
import { ACTION_DESTINATIONS, nextStage, needsTarget, needsWhen, habitMissingDays, committedStatus } from '../types/inbox';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  createBlobSync, mergeCollection, mergeTombstones, pruneTombstones, tombstone, sameRecords,
  matchesCloud, sameTombstones,
  type Tombstones,
} from '../lib/syncBlob';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount, swappingAccount,
} from '../lib/accountStorage';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';

// ─── Supabase sync — inbox items as one blob per user ───────────────────────
// The blob used to hold `{ tasks: Task[] }` (every capture was a Task). It now holds
// `{ items: InboxItem[], deleted: Tombstones }`; legacy `tasks` are migrated on read
// so nothing is lost.
//
// ⚠️ Writes go through syncBlob's read-modify-write, NOT a wholesale upsert. The
// old code PUT the entire local array, so a device holding a stale cache deleted
// records it had never seen — that is what made inbox statuses appear to shuffle
// across restarts (see lib/syncBlob.ts for the full diagnosis).
const USER_INBOX_TABLE = 'user_inbox';

function readTombstones(data: any): Tombstones {
  const raw = data?.deleted;
  return raw && typeof raw === 'object' ? (raw as Tombstones) : {};
}

const inboxSync = createBlobSync({
  transport: supabaseBlobTransport(USER_INBOX_TABLE),
  owner: () => useInboxStore.getState().cachedUserId,
  onError: err => console.error('Failed to sync inbox to Supabase:', err),
  build: (cloud) => {
    const { items, deleted } = useInboxStore.getState();
    const cloudTombs = readTombstones(cloud);
    const merged = pruneTombstones(mergeTombstones(deleted, cloudTombs));
    const nextItems = mergeCollection(items, readBlob(cloud) ?? [], merged);
    // Nothing new to contribute — skip the write instead of echoing the load back.
    if (matchesCloud([[nextItems, cloud?.items]], merged, cloudTombs)) return null;
    return { items: nextItems, deleted: merged };
  },
});

/** Legacy captures were Tasks. Bring them across at the top of the funnel. */
export function migrateLegacyTask(task: Partial<Task> & { id: string; title: string }): InboxItem {
  return {
    id: task.id,
    title: task.title,
    description: task.description || task.notes || undefined,
    kind: 'unclear',
    stage: 'capture',
    status: 'open',
    domain: task.category || undefined,
    createdAt: task.createdAt || new Date().toISOString(),
    updatedAt: task.updatedAt || new Date().toISOString(),
  };
}

/**
 * v2 items carried a single `state` that conflated funnel position with terminal
 * status. Split it: `stage` = where in the funnel, `status` = whether still in it.
 */
export function migrateV2Item(old: any): InboxItem {
  const legacyState = old?.state;
  const map: Record<string, { stage: InboxStage; status: InboxItem['status'] }> = {
    captured:    { stage: 'capture',    status: 'open' },
    clarified:   { stage: 'place',      status: 'open' },   // kind known → next is Place
    prioritized: { stage: 'route',      status: 'open' },
    routed:      { stage: 'route',      status: 'routed' },
    deferred:    { stage: 'prioritize', status: 'parked' },
    archived:    { stage: 'capture',    status: 'archived' },
  };
  const mapped = map[legacyState] || { stage: 'capture' as InboxStage, status: 'open' as const };
  const { state, ...rest } = old || {};
  return { ...rest, stage: old?.stage ?? mapped.stage, status: old?.status ?? mapped.status };
}

/**
 * v3 items kept a single plain-text `note`; v4 splits it into rich-text
 * `description` + `notes`. The old text becomes the description.
 */
export function migrateV3Item(old: any): InboxItem {
  if (!old || !('note' in old)) return old;
  const { note, ...rest } = old;
  return { ...rest, description: rest.description ?? note };
}

/**
 * Description/notes are rich text (HTML), but Tasks and Events store plain strings —
 * strip the markup on the way out rather than leaking tags into their descriptions.
 */
export function plainText(html?: string): string | undefined {
  if (!html) return undefined;
  const text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')                    // list items sit on their own line…
    .replace(/<\/(p|div|h[1-6])>/gi, '\n\n')      // …paragraphs get a blank line between
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return text || undefined;
}

function readBlob(data: any): InboxItem[] | null {
  if (!data) return null;
  if (Array.isArray(data.items)) return (data.items as any[]).map(migrateV2Item).map(migrateV3Item);
  if (Array.isArray(data.tasks)) return (data.tasks as Task[]).map(migrateLegacyTask);
  return null;
}

interface InboxState {
  items: InboxItem[];
  /** id → deletedAt. Without this, the union-on-write would resurrect deletes. */
  deleted: Tombstones;
  initializedUserId: string | null;
  cachedUserId: string | null;
  initialize: () => Promise<void>;

  capture: (title: string, description?: string) => string;
  update: (id: string, updates: Partial<InboxItem>) => void;
  remove: (id: string) => void;

  /** Move an item to a specific funnel stage (also used to step BACK). */
  setStage: (id: string, stage: InboxStage) => void;
  /** Advance one stage along the funnel. No-op at the exit. */
  advance: (id: string) => void;

  clarify: (id: string, kind: InboxKind, outcome?: InboxOutcome) => void;
  categorize: (id: string, domain?: string, links?: { questId?: string; projectId?: string }) => void;
  /** Tag an area within the chosen domain (inbox-only; see InboxItem.area). */
  setArea: (id: string, area?: string) => void;
  prioritize: (id: string, signals: Pick<InboxItem, 'important' | 'urgent' | 'leverage' | 'capacityFit'>) => void;
  /** Pick the action type it should become. */
  classify: (id: string, destination: InboxDestination) => void;
  /** Pick WHERE it lands — an existing project/folder/quest/ability/skill. */
  setTarget: (id: string, target?: InboxTarget) => void;
  defer: (id: string) => void;
  archive: (id: string) => void;
  unpark: (id: string) => void;
  /** Sends the item to its destination, creating a real entity for action types. */
  route: (id: string, destination: InboxDestination) => string | undefined;
  /**
   * Marks the item routed to an entity something ELSE created — the habit form,
   * when the inbox opened it to pick a monthly / yearly habit's days.
   */
  recordRouted: (id: string, destination: InboxDestination, entityId: string) => void;
}

export const useInboxStore = create<InboxState>()(
  persist<InboxState, [], [], any>(
    (set, get) => ({
      items: [],
      deleted: {},
      initializedUserId: null,
      cachedUserId: null,

      initialize: () => dedupeInit('user_inbox', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          if (get().cachedUserId === user.id && isCloudFresh('user_inbox', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }
          const { data, error } = await supabase.from(USER_INBOX_TABLE).select('*');
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;
          const row = (data ?? [])[0];
          set((state) => {
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            const cloudItems = readBlob(row?.data);
            if (cloudItems) {
              const localItems = cacheIsOurs ? state.items : [];
              const localTombs = cacheIsOurs ? state.deleted : {};
              const deleted = pruneTombstones(mergeTombstones(localTombs, readTombstones(row?.data)));
              const items = mergeCollection(localItems, cloudItems, deleted);
              return {
                // Reuse the previous array when nothing actually changed, so the
                // subscription below sees an identical reference and skips a
                // pointless write on every single boot.
                items: sameRecords(items, state.items) ? state.items : items,
                deleted: sameTombstones(deleted, state.deleted) ? state.deleted : deleted,
                initializedUserId: user.id,
                cachedUserId: user.id,
              };
            }
            return cacheIsOurs
              ? { initializedUserId: user.id, cachedUserId: user.id }
              : { items: [], deleted: {}, initializedUserId: user.id, cachedUserId: user.id };
          });
          inboxSync.writeBack(row?.data);
          markCloudFresh('user_inbox', user.id);
        } catch (error) {
          console.error('Failed to initialize inbox from Supabase:', error);
        }
      }),

      capture: (title, description) => {
        const id = crypto.randomUUID();
        const now = new Date().toISOString();
        // Capture stays deliberately dumb: no classification, no domain, no
        // obligation. Deciding what it is happens later, in Clarify.
        const item: InboxItem = {
          id, title: title.trim(), description: description?.trim() || undefined,
          kind: 'unclear', stage: 'capture', status: 'open', createdAt: now, updatedAt: now,
        };
        set(state => ({ items: [item, ...state.items] }));
        return id;
      },

      update: (id, updates) => set(state => ({
        items: state.items.map(i => i.id === id ? { ...i, ...updates, updatedAt: new Date().toISOString() } : i),
      })),

      // ⚠️ Deleting must leave a tombstone. Writes are a union now, so a bare
      // filter would be undone by the very next merge against the cloud copy.
      remove: (id) => set(state => ({
        items: state.items.filter(i => i.id !== id),
        deleted: tombstone(state.deleted, id),
      })),

      setStage: (id, stage) => get().update(id, { stage }),

      advance: (id) => {
        const item = get().items.find(i => i.id === id);
        if (!item) return;
        const next = nextStage(item.stage);
        if (next) get().update(id, { stage: next });
      },

      // ⚠️ Answering a stage no longer moves the item — see the note on STAGE_META.
      // The user steps the funnel explicitly with Back / Next.
      clarify: (id, kind, outcome) => set(state => ({
        items: state.items.map(i => i.id === id ? {
          ...i, kind, outcome,
          clarifiedAt: i.clarifiedAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } : i),
      })),

      categorize: (id, domain, links) => set(state => ({
        items: state.items.map(i => i.id === id ? {
          ...i, domain, ...(links || {}),
          // An area belongs to ONE domain — switching domain makes the old area
          // meaningless, so it is cleared rather than left pointing nowhere.
          area: i.domain === domain ? i.area : undefined,
          updatedAt: new Date().toISOString(),
        } : i),
      })),

      setArea: (id, area) => set(state => ({
        items: state.items.map(i => i.id === id ? {
          ...i, area, updatedAt: new Date().toISOString(),
        } : i),
      })),

      prioritize: (id, signals) => get().update(id, signals),

      classify: (id, destination) => set(state => ({
        items: state.items.map(i => i.id === id ? {
          ...i, destination,
          // Changing the type invalidates a target chosen for the old one — a
          // Quest is not somewhere a Skill can live.
          target: undefined,
          updatedAt: new Date().toISOString(),
        } : i),
      })),

      setTarget: (id, target) => set(state => ({
        items: state.items.map(i => i.id === id ? {
          ...i, target, updatedAt: new Date().toISOString(),
        } : i),
      })),

      defer: (id) => get().update(id, { status: 'parked' }),
      archive: (id) => get().update(id, { status: 'archived' }),
      unpark: (id) => get().update(id, { status: 'open' }),

      route: (id, destination) => {
        const item = get().items.find(i => i.id === id);
        if (!item) return undefined;

        // ⚠️ A nested-only type with nowhere to go must not leave the funnel —
        // there is no standalone Goal/Skill/Requirement to create.
        if (needsTarget(destination, item.target)) return undefined;
        // ⚠️ Nor may a destination that cannot exist without a date (Event) leave
        // without one. The UI disables the commit button; this is the guard that
        // makes the rule true of the store rather than only of the panel.
        if (needsWhen(destination, item)) return undefined;
        // ⚠️ Nor a habit whose frequency has no day — it could never be due
        // (utils/habitSchedule.ts). Weekly is picked in the panel; monthly / yearly
        // go through the habit form, which calls `recordRouted` instead.
        if (habitMissingDays(destination, item)) return undefined;

        let entityId: string | undefined;

        // Attaching into an existing container takes precedence over creating a
        // standalone entity: "a task IN this project" is a different act from
        // "a task".
        if (item.target) {
          entityId = attachInboxItem(destination, item.target, {
            title: item.title,
            description: plainText(item.description),
            category: item.domain || 'personal',
            scheduledFor: item.scheduledFor,
          });
        }

        if (entityId) {
          // handled by the attach path
        } else if (destination === 'task') {
          // ⚠️ taskStore.addTask takes a COMPLETE Task and does NOT generate an id.
          // Omitting it is catastrophic rather than cosmetic: TaskViewer derives the
          // task tree with `tasks.some(c => c.parentTaskId === t.id)`, so a task with
          // BOTH id and parentTaskId undefined matches itself (undefined === undefined),
          // becomes its own parent, and the list renderer recurses until the stack
          // blows. Build the full record — and never re-introduce an `as any` here.
          const now = new Date().toISOString();
          const task: Task = {
            id: crypto.randomUUID(),
            title: item.title,
            description: plainText(item.description),
            category: item.domain || 'personal',
            status: item.scheduledFor ? 'scheduled' : 'categorized',
            completed: false,
            ...(item.scheduledFor ? { dueDate: item.scheduledFor } : {}),
            createdAt: now,
            updatedAt: now,
          };
          entityId = task.id;
          useTaskStore.getState().addTask(task);
        } else if (destination === 'event') {
          // ⚠️ An event is the ONE destination that cannot exist without a date,
          // so it is the one the funnel blocks on (WHEN_SPEC.event.required). It
          // used to fall back to today silently, which invented a calendar entry
          // on a day the user never chose. Route's commit button is disabled
          // until a date is picked; this guard is the belt to that's braces.
          // `needsWhen` already returned above when this is missing; the local is
          // what lets the compiler see it, and it keeps the invariant local to
          // the one branch that depends on it.
          const date = item.scheduledFor;
          if (!date) return undefined;
          useEventStore.getState().addEvent({
            title: item.title,
            description: plainText(item.description),
            date,
            startTime: '09:00',
            endTime: '10:00',
            category: item.domain || 'personal',
          });
        } else if (ACTION_DESTINATIONS.includes(destination)) {
          routeInboxAction(destination as RoutableType, {
            title: item.title,
            description: plainText(item.description),
            category: item.domain || 'personal',
            ...(item.area ? { area: item.area } : {}),
            ...(item.scheduledFor ? { scheduledFor: item.scheduledFor } : {}),
            ...(item.scheduledUntil ? { scheduledUntil: item.scheduledUntil } : {}),
            ...(item.recurrence ? { recurrence: item.recurrence } : {}),
            ...(item.durationDays ? { durationDays: item.durationDays } : {}),
          });
        }
        // 'reference' | 'someday' | 'none' create nothing — the item simply stops
        // being an open loop, which is a legitimate outcome (see NON_ACTION_STATUS).

        get().recordRouted(id, destination, entityId as string);
        return entityId;
      },

      recordRouted: (id, destination, entityId) => {
        set(state => ({
          items: state.items.map(i => i.id === id ? {
            ...i,
            destination,
            routedEntityId: entityId,
            stage: 'route',
            // ⚠️ Only a real action type can be `routed`. Reference / someday /
            // no-action produce nothing outside the inbox, so they park or
            // archive instead — counting them as "became something real" would
            // make the funnel's headline number a lie.
            status: committedStatus(destination),
            routedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          } : i),
        }));
      },
    }),
    {
      name: 'inbox-storage',
      // ⭐ One saved copy per account (lib/accountStorage) — see the note at the bottom.
      storage: accountStorage(cachedUserIdOwner),
      version: 4,
      // `deleted` MUST be persisted: a tombstone that dies on reload lets the next
      // merge resurrect the record it was suppressing.
      partialize: (state: InboxState) =>
        ({ items: state.items, deleted: state.deleted, cachedUserId: state.cachedUserId }) as any,
      migrate: (persisted: any, version: number) => {
        let next = persisted;
        // v1 persisted `tasks: Task[]`; v2+ persists `items: InboxItem[]`.
        if (version < 2 && next && Array.isArray(next.tasks)) {
          next = { ...next, items: next.tasks.map(migrateLegacyTask), tasks: undefined };
        }
        // v2 items had a single `state`; v3 splits it into `stage` + `status`.
        if (version < 3 && next && Array.isArray(next.items)) {
          next = { ...next, items: next.items.map(migrateV2Item) };
        }
        // v3 had one plain `note`; v4 splits it into rich `description` + `notes`.
        if (version < 4 && next && Array.isArray(next.items)) {
          next = { ...next, items: next.items.map(migrateV3Item) };
        }
        return next;
      },
    }
  )
);

// Auto-sync on change. No suppression flag is needed any more: initialize()
// hands back the SAME references when its merge changed nothing, and decides its
// own write-back against the cloud copy (`writeBack`). A change of account swaps
// the saved copy in — not an edit.
useInboxStore.subscribe((state, prev) => {
  if (swappingAccount()) return;
  if (state.items === prev.items && state.deleted === prev.deleted) return;
  inboxSync.schedule();
});

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with
   the account it was loaded for, and a sign-out does not reload the page: an account's
   captures whose write had not landed lived only until the next account's load replaced
   the copy, and the next account's captures made before its own load sat in the copy
   tagged with the last one — whose next load merged them into ITS row. */
scopeToAccount(useInboxStore);
